/** Keep in sync with server/src/account-chart/account-chart-credit-card.helpers.ts */

export const DEFAULT_CREDIT_CARD_ACCOUNT_KEY = '2.01.01.03';

/** Matches direct children of 2.01.01 only (e.g. 2.01.01.01, 2.01.01.03). */
export const DIRECT_CREDIT_CARD_ACCOUNT_KEY_PATTERN = /^2\.01\.01\.[^.]+$/;

export const isDirectCreditCardAccountKey = (key) =>
  DIRECT_CREDIT_CARD_ACCOUNT_KEY_PATTERN.test(String(key ?? '').trim());

export const getAccountCreditCardIdsFromNode = (account) => {
  if (!account) return [];
  const raw = Array.isArray(account.creditCardIds) ? account.creditCardIds : [];
  const ids = raw
    .map((id) => (id != null && id !== '' ? String(id).trim() : null))
    .filter(Boolean);
  return [...new Set(ids)];
};

/**
 * Builds a Map<cardId, accountKey> from the flat/nested account chart.
 * Multiple cards can map to the same account key.
 */
export const buildCreditCardIdToAccountKeyMap = (accounts) => {
  const map = new Map();
  const walk = (nodes) => {
    if (!Array.isArray(nodes) || !nodes.length) return;
    for (const node of nodes) {
      const key = String(node?.key ?? '').trim();
      if (key && isDirectCreditCardAccountKey(key)) {
        for (const cid of getAccountCreditCardIdsFromNode(node)) {
          map.set(cid, key);
        }
      }
      if (node?.children?.length) walk(node.children);
    }
  };
  walk(accounts);
  return map;
};

/**
 * Resolves the GL account key for a given cardId.
 * If not found in the map, returns the fallback (default: 2.01.01.03).
 *
 * @param {string|null} cardId
 * @param {Array} accounts
 * @param {string} [fallbackKey]
 * @param {Map} [map] - pre-built map (optional, built on the fly when not provided)
 */
export const resolveCreditCardAccountKey = (
  cardId,
  accounts = [],
  fallbackKey = DEFAULT_CREDIT_CARD_ACCOUNT_KEY,
  map = null,
) => {
  const cid = cardId != null && cardId !== '' ? String(cardId).trim() : null;
  if (!cid) return fallbackKey;
  const resolvedMap = map instanceof Map ? map : buildCreditCardIdToAccountKeyMap(accounts);
  return resolvedMap.get(cid) ?? fallbackKey;
};

/**
 * Validates that each credit card is assigned to at most one account.
 * `editedCreditCardIdsByKey` is a plain object: { [accountKey]: string[] }.
 * Returns an error message string, or null if valid.
 *
 * @param {Array} accounts - current account chart (used for existing assignments not yet in edit state)
 * @param {Object} editedCreditCardIdsByKey - changes being made in the UI
 * @returns {string|null}
 */
export const validateCreditCardAssignments = (accounts, editedCreditCardIdsByKey = {}) => {
  const ownerByCardId = new Map();

  // Gather assignments from non-edited accounts
  const walk = (nodes) => {
    if (!Array.isArray(nodes)) return;
    for (const node of nodes) {
      const key = String(node?.key ?? '').trim();
      if (!isDirectCreditCardAccountKey(key)) {
        if (node?.children?.length) walk(node.children);
        continue;
      }
      const editedIds = editedCreditCardIdsByKey[key];
      const ids =
        editedIds !== undefined
          ? editedIds.map((id) => String(id ?? '').trim()).filter(Boolean)
          : getAccountCreditCardIdsFromNode(node);

      for (const cid of ids) {
        const existing = ownerByCardId.get(cid);
        if (existing && existing !== key) {
          return `La tarjeta está asignada a más de una cuenta contable (${existing} y ${key}).`;
        }
        ownerByCardId.set(cid, key);
      }

      if (node?.children?.length) walk(node.children);
    }
  };
  walk(accounts);

  // Also check keys that are in editedCreditCardIdsByKey but may not have been visited
  for (const [key, ids] of Object.entries(editedCreditCardIdsByKey)) {
    for (const rawId of ids ?? []) {
      const cid = String(rawId ?? '').trim();
      if (!cid) continue;
      const existing = ownerByCardId.get(cid);
      if (existing && existing !== key) {
        return `La tarjeta está asignada a más de una cuenta contable (${existing} y ${key}).`;
      }
      ownerByCardId.set(cid, key);
    }
  }

  return null;
};

/**
 * Parses a cardId from a credit card obligation period string.
 * Format: "YYYYMM:cardId" (e.g. "202501:abc123def456")
 *
 * @param {string|null|undefined} period
 * @returns {string|null}
 */
export const parseCreditCardIdFromPeriod = (period) => {
  if (!period) return null;
  const s = String(period).trim();
  const colonIdx = s.indexOf(':');
  if (colonIdx === -1) return null;
  const cardId = s.substring(colonIdx + 1).trim();
  return cardId || null;
};
